import * as React           from "react";
import {createElement as h} from "react";

import {Types}                from "../types";
import {last, sort}           from "../utils/collections";
import {exclude12, intersect} from "../utils/ranges";

import {cst}      from "./chain-services";
import {Invoke}   from "./invoke";
import {UseState} from "./use-state";

export const RangeLoader = <Load, Item>({_key, range: _range, load, mapLoadAsItems, mapHasMore, next, disabled=false}:{
    _key            : React.Key,
    range           : {from: number, to: number}, // TODO: Enhance range types
    load            : (range:{from: number, to: number}) => Promise<Load>,
    mapLoadAsItems ?: (load:Load) => Item[],
    mapHasMore     ?: (load:Load) => boolean,
    next            : (v:Types.Common.React.RangeLoader.Next<Load, Item>) => JSX.Element,
    disabled       ?: boolean,
}) => cst<{
    loadedRanges : Types.Common.React.UseState.UseStateNext<Array<{key: string, range: {from: number, to: number}, loaded: Load}>>
}>(
    ["loadedRanges", (_, next) => UseState({initValue: [], next})],
    ({loadedRanges}) => {
        const sKey                  = JSON.stringify(_key);
        const keyLoadedRanges       = loadedRanges.value.filter((r) => r.key === sKey);
        const sortedKeyLoadedRanges = sort(keyLoadedRanges, (r) => r.range.from);

        const loadingRanges = exclude12(
            _range,
            keyLoadedRanges.map((r) => r.range),
        );

        return (<>
            {!disabled && loadingRanges.map((uncoveredRange) => (
                h(Invoke, {
                    key: JSON.stringify({_key, uncoveredRange}),
                    fn: async ({isMounted}) => {

                        const loaded = await load(uncoveredRange);

                        if (!isMounted()) {
                            return;
                        }

                        loadedRanges.change((loadedRanges) => [
                            ...loadedRanges,
                            {
                                key: sKey,
                                range: uncoveredRange,
                                loaded,
                            }
                        ]);
                    },
                })
            ))}

            {next({
                list: sortedKeyLoadedRanges.map((r) => r.loaded),
                loading: !!loadingRanges.length,
                reload: () => loadedRanges.onChange([]),

                ...mapLoadAsItems && {
                    getItemsInRange: (range=_range) => {
                        const intersectingLoads = sortedKeyLoadedRanges.filter(({range: entryRange}) => intersect(entryRange, range));
                        return intersectingLoads.reduce<Item[]>(
                            (items, {loaded, range: entryRange}) => {
                                const entryItems  = mapLoadAsItems(loaded);
                                if (entryItems.length !== entryRange.to - entryRange.from)
                                    console.warn("Range count mismatch", {range: entryRange, items: entryItems});

                                const entryOffset     = entryRange.from - range.from;
                                const insertionIndex  = Math.max(0, entryOffset);
                                const entrySliceIndex = Math.max(0, -entryOffset);
                                const entryCountUsed  = Math.min(items.length - insertionIndex, entryItems.length);
                                items.splice(
                                    insertionIndex,
                                    entryCountUsed,
                                    ...entryItems.slice(entrySliceIndex, entrySliceIndex + entryCountUsed)
                                );
                                return items;
                            },
                            new Array(range.to - range.from)
                        );
                    },
                },
                ...mapHasMore && {hasMore: mapHasMore(last(loadedRanges?.value)?.loaded)}
            })}
        </>);
    }
);

